import { useEffect, useRef } from "react";
import { TranslationFeKey } from "shared/types/translation-key";

import { useConversationState } from "./conversation-state-provider";
import { ChatMessageComponent } from "~/components/chat/generic/chat-message";
import { ChatMessage } from "~/components/chat/types";
import { Text } from "~/components/text";
import { cn } from "~/util/cn";

export type MessageListProps = {
  chatMessages: ChatMessage[];
  chatLoadingMessage?: TranslationFeKey;
  shouldShowDisclaimer?: boolean;
  className?: string;
  initialScrollDelay?: number;
};

export const MessageList: React.FC<MessageListProps> = ({
  chatMessages,
  chatLoadingMessage,
  shouldShowDisclaimer = false,
  className,
  initialScrollDelay = 0,
}) => {
  const scrollableRef = useRef<HTMLElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { isChatBusy } = useConversationState();

  const scrollToBottom = (smooth = true) => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({
        top: scrollableRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  const hasInitialScrollRef = useRef(false);

  useEffect(() => {
    if (hasInitialScrollRef.current) return;

    const initialScrollTimer = setTimeout(() => {
      scrollToBottom(true);
      hasInitialScrollRef.current = true;
    }, initialScrollDelay);

    return () => clearTimeout(initialScrollTimer);
  }, [initialScrollDelay]);

  useEffect(() => {
    if (!hasInitialScrollRef.current || chatMessages.length === 0) return;
    scrollToBottom(true);
  }, [chatMessages]);

  return (
    <main
      ref={scrollableRef}
      className={cn(
        !shouldShowDisclaimer && "pb-20",
        "px-3 pt-9",
        "flex h-full flex-col justify-start gap-4 overflow-y-auto overflow-x-hidden sm:gap-6",
        "bg-gradient-to-b from-base-200/20 to-transparent",
      )}
    >
      {chatMessages.map((chatMessage, index) => (
        <div
          key={index}
          ref={index === chatMessages.length - 1 ? chatContainerRef : undefined}
          className={cn("flex w-full", className)}
        >
          <ChatMessageComponent
            message={chatMessage}
            media={chatMessage.media}
          />
        </div>
      ))}
      {isChatBusy && (
        <div
          className={cn(
            "flex w-full max-w-screen-sm items-start justify-start pb-2 pt-4 animate-slideIn",
            className,
          )}
        >
          <span className="loading loading-ring loading-md mr-2" />
          <Text
            tx={chatLoadingMessage ?? "chat.loading"}
            className="text-sm text-mama-default-primary"
          />
        </div>
      )}
      {shouldShowDisclaimer ? (
        <Text
          as="p"
          tx="legal.mamaAskDisclaimer"
          className={cn(
            "mx-auto -mt-2 pb-9 pl-2 sm:-mt-4",
            "w-full max-w-screen-sm gap-1",
            "text-left text-xs font-normal text-slate-400",
            "transition-opacity ease-in-out",
            isChatBusy ? "opacity-0 duration-75" : "opacity-100 duration-500",
          )}
        />
      ) : null}
    </main>
  );
};
